// eslint-disable-file allow-null

import { GregorioGlyphType } from '../../gregorio-chant/enums/GregorioGlyphType';
import { GregorioLiquescentia } from '../../gregorio-chant/enums/GregorioLiquescentia';
import { GregorioShape } from '../../gregorio-chant/enums/GregorioShape';
import { GregorioSign } from '../../gregorio-chant/enums/GregorioSign';
import { GregorioSpace } from '../../gregorio-chant/enums/GregorioSpace';
import { GregorioType } from '../../gregorio-chant/enums/GregorioType';
import { GregorioVPosition } from '../../gregorio-chant/enums/GregorioVPosition';
import { GregorioGlyph } from '../../gregorio-chant/structures/GregorioGlyph';
import { GregorioNoteElement, HEPISEMA } from '../../gregorio-chant/structures/GregorioNoteElement';
import { GregorioElement } from '../../gregorio-chant/structures/GregorioSyllable';
import { GregorioScore } from '../../public-api';
import { HEPISEMA_FIRST_TWO, LOW_LEDGER_LINE_PITCH, NO_PITCH } from '../../gregorio-chant/constants';
import { GregorioHepisemaAdjustment, GregorioSignOrientation, GrehepisemaSize, GtexType } from './enums';
import { GregorioStruct } from './hepisema_adjustments';
import { compute_fused_shift } from '../../gregorio-chant/score_parsing/compute_fused_shift';
import { gregoriotex_determine_glyph_name } from '../glyphs/determine_glyph';

const gregStruct = new GregorioStruct();
export const UNDETERMINED_HEIGHT = -127;

function first_note_case(current_note: GregorioNoteElement, next: GregorioNoteElement,
  current_glyph: GregorioGlyph): string {
  let ambitus_one = true;
  if (current_glyph.notes.liquescentia >= GregorioLiquescentia.INITIO_DEBILIS) {
    return 'InitioDebilis';
  } else {
    if (next && next.pitch - current_note.pitch != 1 && current_note.pitch - next.pitch != 1) {
      ambitus_one = false;
    }
    switch (current_note.shape) {
      case GregorioShape.ORISCUS_ASCENDENS:
      case GregorioShape.ORISCUS_DESCENDENS:
        return ambitus_one ? 'InitialOriscus' : 'InitialConnectedOriscus';

      case GregorioShape.QUILISMA:
        return ambitus_one ? 'InitialQuilisma' : 'InitialConnectedQuilisma';

      default:
        if (ambitus_one) {
          return 'InitialPunctum';
        }
        if (current_note.pitch > next.pitch) {
          return 'InitialConnectedVirga';
        } else {
          return 'InitialConnectedPunctum';
        }
    }
  }
}

function fused_single_note_case(glyph: GregorioGlyph, unfused_case: string, fused_case: string): string {
  const fuse_to_next_glyph = glyph.notes.fuse_to_next_glyph;
  if (fuse_to_next_glyph < -1 || fuse_to_next_glyph > 1) {
    return fused_case;
  }
  return unfused_case;
}

function is_bridgeable_space(element: GregorioElement): boolean {
  if (element.type === GregorioType.SPACE) {
    switch (element.misc.info.space) {
      case GregorioSpace.NEUMATIC_CUT:
      case GregorioSpace.LARGER_SPACE:
      case GregorioSpace.NEUMATIC_CUT_NB:
      case GregorioSpace.LARGER_SPACE_NB:
        return true;
      default:
        break;
    }
  }

  return false;
}

function start_h_episema(
  h: HeightComputation,
  element: GregorioElement,
  element_i: number,
  glyph: GregorioGlyph,
  glyph_i: number,
  note: GregorioNoteElement,
  note_i: number,
  next: GregorioNoteElement,
  next_next: GregorioNoteElement,
): void {
  // debugger;
  h.start_element_i = element_i;
  h.start_glyph_i = glyph_i;
  h.start_note_i = note_i;
  h.active = true;
  h.height = compute_h_episema_height(glyph, note, next, next_next, h.vpos);
}

function low_high_set_lower(glyph: GregorioGlyph, note: GregorioNoteElement): void {
  if ((glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) ||
    !(glyph.notes.liquescentia & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS))) {
    note.is_lower_note = true;
  }
}

function above_if_h_episema(note: GregorioNoteElement | undefined): GregorioVPosition {
  // eslint-disable-next-line max-len
  if (note && note.h_episema_above && (note.h_episema_above_size || note.h_episema_above_size === GrehepisemaSize.H_NORMAL)) {
    return GregorioVPosition.ABOVE;
  }
  return GregorioVPosition.BELOW;
}

class HeightComputation {
  vpos!: GregorioVPosition;
  orientation!: GregorioSignOrientation;
  elements!: GregorioElement[];

  is_applicable!: (note: GregorioNoteElement) => boolean;
  is_shown!: (note: GregorioNoteElement) => boolean;
  is_connected!: (note: GregorioNoteElement) => boolean;
  get_size!: (note: GregorioNoteElement) => GrehepisemaSize;
  is_better_height!: (height1: number, height2: number) => boolean;
  position!: (note: GregorioNoteElement, height: number, active: boolean) => void;

  active!: boolean;
  height!: number;
  connected!: boolean;
  start_element_i!: number | null;
  start_glyph_i!: number | null;
  start_note_i!: number | null;
  last_connected_element_i!: number | null;
  last_connected_glyph_i!: number | null;
  last_connected_note_i!: number | null;

  constructor(init: any) {
    Object.assign(this, init);
  }

  get start_note(): GregorioNoteElement | null {
    if (this.start_glyph && (this.start_note_i || this.start_note_i === 0)) {
      return this.start_glyph.notes.notes[this.start_note_i];
    }
    return null;
  }

  get start_glyph(): GregorioGlyph | null {
    if (this.start_element && (this.start_glyph_i || this.start_glyph_i === 0)) {
      return this.start_element.glyphs[this.start_glyph_i];
    }
    return null;
  }

  get start_element(): GregorioElement | null {
    if (this.elements && (this.start_element_i || this.start_element_i === 0)) {
      return this.elements[this.start_element_i];
    }
    return null;
  }

  get last_connected_note(): GregorioNoteElement | undefined {
    if (this.last_connected_glyph && (this.last_connected_note_i || this.last_connected_note_i === 0)) {
      return this.last_connected_glyph.notes.notes[this.last_connected_note_i];
    }
    // throw new Error('last_connected_note is null');
    // return;
  }

  get last_connected_glyph(): GregorioGlyph | null {
    if (this.last_connected_element && (this.last_connected_glyph_i || this.last_connected_glyph_i === 0)) {
      return this.last_connected_element.glyphs[this.last_connected_glyph_i];
    }
    return null;
  }

  get last_connected_element(): GregorioElement | null {
    if (this.elements && (this.last_connected_element_i || this.last_connected_element_i === 0)) {
      return this.elements[this.last_connected_element_i];
    }
    return null;
  }
}


function is_fused(liquescentia: number): boolean {
  return (liquescentia & GregorioLiquescentia.FUSED) !== 0;
}

export function compute_note_positioning(
  above: HeightComputation,
  below: HeightComputation,
  element: GregorioElement,
  element_i: number,
  glyph_i: number,
  note: GregorioNoteElement,
  next: GregorioNoteElement,
  next_next: GregorioNoteElement,
  note_i: number,
  type: GtexType,
  score: GregorioScore,
  elements: GregorioElement[],
) {
  const default_vpos: GregorioVPosition = advise_positioning(element.glyphs, glyph_i, note, note_i, type);

  if (note.h_episema_above === HEPISEMA.AUTO && note.h_episema_below === HEPISEMA.AUTO) {
    if (default_vpos === GregorioVPosition.BELOW) {
      note.h_episema_above = HEPISEMA.NONE;
    } else {
      note.h_episema_below = HEPISEMA.NONE;
    }
  }

  const glyph = element.glyphs[glyph_i];
  compute_h_episema(above, element, element_i, glyph, glyph_i, note, next, next_next, note_i, type, score, elements);
  compute_h_episema(below, element, element_i, glyph, glyph_i, note, next, next_next, note_i, type, score, elements);
}

function is_connected_left(size: GrehepisemaSize): boolean {
  return size === GrehepisemaSize.H_NORMAL || size === GrehepisemaSize.H_SMALL_LEFT;
}

function below_if_auctus(glyph: GregorioGlyph): GregorioVPosition {
  if (glyph.notes.liquescentia & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
    return GregorioVPosition.BELOW;
  }
  return GregorioVPosition.ABOVE;
}

function choral_sign_here_is_low(
  glyph: GregorioGlyph,
  note: GregorioNoteElement,
  next: GregorioNoteElement,
  next_next: GregorioNoteElement,
  kind_of_pes: { value: boolean } | null,
): boolean {
  if (kind_of_pes) {
    kind_of_pes.value = false;
  }

  switch (glyph.notes.glyph_type) {
    case GregorioGlyphType.FLEXA:
    case GregorioGlyphType.TORCULUS:
    case GregorioGlyphType.TORCULUS_LIQUESCENS:
    case GregorioGlyphType.TORCULUS_RESUPINUS_FLEXUS:
    case GregorioGlyphType.PORRECTUS_FLEXUS:
      if (!next) {
        return true;
      }
      break;

    case GregorioGlyphType.PODATUS:
    case GregorioGlyphType.PORRECTUS:
    case GregorioGlyphType.TORCULUS_RESUPINUS:
      if (!next || next_next) {
        break;
      }
      if (kind_of_pes) {
        kind_of_pes.value = true;
      }
      return true;

    default:
      break;
  }

  return false;
}

function compute_h_episema_height(
  glyph: GregorioGlyph,
  note: GregorioNoteElement,
  next: GregorioNoteElement,
  next_next: GregorioNoteElement,
  vpos: GregorioVPosition
): number {
  let height = note.pitch;

  if (note.signs & GregorioSign.V_EPISEMA) {
    if ((vpos === GregorioVPosition.ABOVE && note.v_episema_height >= height)
      || (vpos === GregorioVPosition.BELOW && note.v_episema_height <= height)) {
      height = note.v_episema_height;
    }
  }

  if (vpos === GregorioVPosition.ABOVE && note.choral_sign
    && !choral_sign_here_is_low(glyph, note, next, next_next, null)) {
    ++height;
    if (!is_on_a_line(note.pitch)) {
      ++height;
    }
  }

  return height + vpos;
}

function is_connectable_interglyph_notes(
  first: GregorioNoteElement | null,
  second: GregorioNoteElement | null
): boolean {
  return first !== null && second !== null && is_connectable_interglyph_ambitus(
    first.pitch, second.pitch);
}

function has_space_to_left(note: GregorioNoteElement, previous: GregorioNoteElement): boolean {
  switch (note.shape) {
    case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
    case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
    case GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS:
    case GregorioShape.PUNCTUM_INCLINATUM_AUCTUS:
      return !is_connectable_interglyph_notes(previous, note);

    default:
      return !previous;
  }
}

function is_connectable_interglyph_ambitus(first: number, second: number): boolean {
  return Math.abs(first - second) < 3;
}

function note_before_last_note_case(current_glyph: GregorioGlyph, current_note: GregorioNoteElement,
  next: GregorioNoteElement, previous: GregorioNoteElement): string {
  if ((current_glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) && next) {
    return 'PenultBeforeDeminutus';
  } else {
    return note_before_last_note_case_ignoring_deminutus(current_note, next, previous);
  }
}

function gregorio_glyph_last_note(glyph: GregorioGlyph | null): GregorioNoteElement | undefined {
  if (!(glyph && glyph.type == GregorioType.GLYPH)) {
    // throw new Error('trying to find the last note of something that is not a glyph');
    return;
  }
  return glyph.notes.notes[glyph.notes.notes.length - 1];
}

function end_h_episema(
  h: HeightComputation,
  glyph: GregorioGlyph,
  end: GregorioNoteElement | null,
  end_note_i: number | null,
  end_glyph_i: number | null,
  end_element_i: number | null,
  score: GregorioScore,
  elements: GregorioElement[],
): void {
  let proposed_height: number;

  if (h.active) {
    if (is_connected_left(h.get_size(h.start_note!))) {
      if (h.start_note_i! > 0) {
        const previous = h.start_glyph!.notes.notes[h.start_note_i! - 1];
        if (!has_space_to_left(h.start_note!, previous)
          && previous.type === GregorioType.NOTE
          && is_connected_right(h.get_size(previous))) {
          proposed_height = previous.pitch + h.vpos;
          if (h.is_better_height(proposed_height, h.height)) {
            h.height = proposed_height;
          }
        }
      } else {
        const previous = gregorio_previous_non_texverb_glyph(h.start_element!.glyphs, h.start_glyph_i!);
        if (previous && previous.type === GregorioType.GLYPH
          && is_fused(h.start_glyph!.notes.liquescentia)) {
          const previousGlyph = h.start_element!.glyphs[h.start_glyph_i! - 1];
          const note = gregorio_glyph_last_note(previousGlyph);
          if (note?.type === GregorioType.NOTE
            && is_connected_right(h.get_size(note))) {
            proposed_height = note.pitch + h.vpos;
            if (h.is_better_height(proposed_height, h.height)) {
              h.height = proposed_height;
            }
          }
        }
      }
    }
    const end_previous = glyph.notes.notes[end_note_i! - 1];
    if (end && end_note_i && end.type === GregorioType.NOTE && is_connected_left(h.get_size(end))
      && !(end.liquescentia & GregorioLiquescentia.DEMINUTUS)
      && h.last_connected_note
      && is_connected_right(h.get_size(h.last_connected_note))) {
      if (h.last_connected_note_i! < h.last_connected_glyph!.notes.notes.length - 1) {
        const last_connected_note_next = h.last_connected_glyph!.notes.notes[h.last_connected_note_i! + 1];
        // if (h.last_connected_note.next) {
        if (last_connected_note_next.type === GregorioType.NOTE
          && !has_space_to_left(last_connected_note_next, h.last_connected_note)) {
          for (let note_i = end_note_i - 1; note_i > -1; note_i--) {
            const note = glyph.notes.notes[note_i];
            if (note === h.start_note) {
              proposed_height = end.pitch + h.vpos;
              if (h.is_better_height(proposed_height, h.height)) {
                h.height = proposed_height;
              }
              break;
            }
          }
        }
      } else if (is_fused(end.liquescentia) && !end_previous) {
        proposed_height = end.pitch + h.vpos;
        if (h.is_better_height(proposed_height, h.height)) {
          h.height = proposed_height;
        }
      }
    }

    set_h_episema_height(h, end_note_i!, end_glyph_i, end_element_i, score, elements);

    h.active = false;
    h.height = 0;
    h.connected = false;
    h.start_element_i = null;
    h.start_glyph_i = null;
    h.start_note_i = null;
    h.last_connected_element_i = null;
    h.last_connected_glyph_i = null;
    h.last_connected_note_i = null;

    // if (h.orientation === GregorioSignOrientation.SO_OVER) {
    //   Object.assign(h, empty_above_computation(h.elements));
    // } else {
    //   Object.assign(h, empty_below_computation(h.elements));
    // }
    // console.log(h);
  }
}

function has_high_ledger_line(
  height: number,
  is_sign: boolean,
  score: GregorioScore,
): boolean {
  if (is_sign) {
    return height > score.high_ledger_line_pitch;
  }
  return height >= score.high_ledger_line_pitch;
}

function has_low_ledger_line(
  height: number,
  is_sign: boolean
): boolean {
  if (is_sign) {
    return height < LOW_LEDGER_LINE_PITCH;
  }
  return height <= LOW_LEDGER_LINE_PITCH;
}

function gregorio_previous_non_texverb_glyph(glyphs: GregorioGlyph[], current_index: number): GregorioGlyph | null {
  for (let i = current_index - 1; i >= 0; i--) {
    if (glyphs[i].type != GregorioType.TEXVERB_GLYPH) {
      return glyphs[i];
    }
  }
  return null;
}

function gregorio_next_non_texverb_glyph(glyphs: GregorioGlyph[], current_index: number): GregorioGlyph | null {
  for (let i = current_index + 1; i < glyphs.length; i++) {
    if (glyphs[i].type != GregorioType.TEXVERB_GLYPH) {
      return glyphs[i];
    }
  }
  return null;
}

function above_if_fused_next_h_episema(glyphs: GregorioGlyph[], current_index: number): GregorioVPosition {
  const next = gregorio_next_non_texverb_glyph(glyphs, current_index);
  // if (!next || next.type !== GregorioType.GLYPH && is_fused(next.notes.liquescentia)) {
  // throw new Error('expected this glyph to be fused to the next one');
  // }
  return above_if_h_episema(next?.notes.first_note);
}

function is_connected_right(size: GrehepisemaSize): boolean {
  return size === GrehepisemaSize.H_NORMAL || size === GrehepisemaSize.H_SMALL_RIGHT;
}

function next_has_ledger_line(
  h: HeightComputation,
  elements: GregorioElement[],
  high_ledger_line_arg: boolean,
  low_ledger_line_arg: boolean
): void {
  let element_i = h.last_connected_element_i!;
  let glyph_i = h.last_connected_glyph_i!;
  let note_i = h.last_connected_note_i!;
  let element = h.last_connected_element;
  let glyph = h.last_connected_glyph;
  let note = h.last_connected_note;

  let eat_upper_note = note!.is_lower_note, keep_going = false;
  let high_ledger_line = high_ledger_line_arg;
  let low_ledger_line = low_ledger_line_arg;

  if (!high_ledger_line || !low_ledger_line) {
    do {
      note_i++;
      if (glyph!.notes.notes.length === note_i) {
        note_i = 0;
        do {
          // glyph = glyph.next;
          glyph_i++;
          if (element!.glyphs.length === glyph_i) {
            glyph_i = 0;
            do {
              element_i++;
              if (elements.length === element_i) {
                return;
              }
              element = elements[element_i];
            } while (element.type !== GregorioType.ELEMENT);
            glyph = element.glyphs[0];
          } else {
            glyph = element!.glyphs[glyph_i];
          }
        } while (glyph!.type !== GregorioType.GLYPH);
        note = glyph!.notes.first_note;
      }

      note = glyph!.notes.notes[note_i];

      high_ledger_line = high_ledger_line || note!.high_ledger_line;
      low_ledger_line = low_ledger_line || note!.low_ledger_line;

      if (keep_going) {
        keep_going = false;
      }
      if (eat_upper_note && note!.is_upper_note) {
        eat_upper_note = false;
        keep_going = true;
      }
    } while ((!high_ledger_line || !low_ledger_line)
      && (note!.is_lower_note || keep_going));
  }
}

function previous_has_ledger_line(
  h: HeightComputation,
  elements: GregorioElement[],
  high_ledger_line_arg: boolean,
  low_ledger_line_arg: boolean
): void {
  let element_i = h.last_connected_element_i!;
  let glyph_i = h.last_connected_glyph_i!;
  let note_i = h.last_connected_note_i!;
  let element = h.last_connected_element;
  let glyph = h.last_connected_glyph;
  let note = h.last_connected_note;

  let high_ledger_line = high_ledger_line_arg;
  let low_ledger_line = low_ledger_line_arg;
  let eat_lower_note = note!.is_upper_note, keep_going = false;

  if (!high_ledger_line || !low_ledger_line) {
    do {
      note_i--;
      // note = note.previous;
      if (note_i < 0) {
        note_i = 0;
        do {
          glyph_i--;
          // glyph = glyph.previous;
          if (glyph_i < 0) {
            glyph_i = 0;
            do {
              element_i--;
              // element = element.previous;
              if (element_i < 0) {
                return;
              }
              element = elements[element_i];
            } while (element.type !== GregorioType.ELEMENT);
            glyph = element.glyphs[element.glyphs.length - 1];
          } else {
            glyph = element!.glyphs[glyph_i];
          }
        } while (glyph!.type !== GregorioType.GLYPH);
        note = gregorio_glyph_last_note(glyph);
      }
      note = glyph!.notes.notes[note_i];

      high_ledger_line = high_ledger_line || note!.high_ledger_line;
      low_ledger_line = low_ledger_line || note!.low_ledger_line;

      if (keep_going) {
        keep_going = false;
      }
      if (eat_lower_note && note!.is_lower_note) {
        eat_lower_note = false;
        keep_going = true;
      }
    } while ((!high_ledger_line || !low_ledger_line)
      && (note!.is_upper_note || keep_going));
  }
}

function set_h_episema_height(
  h: HeightComputation,
  end_note_i: number,
  end_glyph_i: number | null,
  end_element_i: number | null,
  score: GregorioScore,
  elements: GregorioElement[],
): void {
  let last_note: GregorioNoteElement | null = null;

  const start_element_i = h.start_element_i;
  const start_glyph_i = h.start_glyph_i;
  const start_note_i = h.start_note_i;

  const high_ledger_line = has_high_ledger_line(h.height, true, score)
    || has_high_ledger_line(h.height - h.vpos, false, score);
  const low_ledger_line = has_low_ledger_line(h.height, true)
    || has_low_ledger_line(h.height - h.vpos, false);

  next_has_ledger_line(h, elements, high_ledger_line, low_ledger_line);
  previous_has_ledger_line(h, elements, high_ledger_line, low_ledger_line);


  for (let el_i = start_element_i!; el_i < elements.length; el_i++) {
    const element = elements[el_i];
    if (element.type === GregorioType.ELEMENT) {
      for (let glyph_i = start_glyph_i ? start_glyph_i : 0; glyph_i < element.glyphs.length; glyph_i++) {
        const glyph = element.glyphs[glyph_i];
        if (glyph && glyph.type === GregorioType.GLYPH) {
          for (let note_i = start_note_i ? start_note_i : 0; note_i < glyph.notes.notes.length; note_i++) {
            // eslint-disable-next-line max-len
            if ((note_i || note_i === 0) && note_i >= end_note_i || ((end_glyph_i || end_glyph_i === 0) && glyph_i > end_glyph_i) || ((end_element_i || end_element_i === 0) && el_i > end_element_i)) {
              if (last_note) {
                position_h_episema(last_note, h, false, high_ledger_line, low_ledger_line);
              }
              return;
            }
            const note = glyph.notes.notes[note_i];
            if (h.is_applicable(note)) {
              position_h_episema(note, h, true, high_ledger_line, low_ledger_line);
              last_note = note;
            }
          }
        }
      }
    } else if (!is_bridgeable_space(element)) {
      break;
    }
  }

  if (last_note) {
    position_h_episema(last_note, h, false, high_ledger_line,
      low_ledger_line);
  }
}

function is_on_a_line(pitch: number): boolean {
  return pitch % 2 === 0;
}

function gregorio_get_hepisema_adjustment(index: number): GregorioHepisemaAdjustment | undefined {
  return gregStruct.gregorio_get_hepisema_adjustment(index);
}

function position_h_episema(
  note: GregorioNoteElement,
  h: HeightComputation,
  connect: boolean,
  high_ledger_line: boolean,
  low_ledger_line: boolean
): void {
  h.position(note, h.height, connect);
  if (!note.high_ledger_specificity && !note.high_ledger_line) {
    note.high_ledger_line = high_ledger_line;
  }
  if (!note.low_ledger_specificity && !note.low_ledger_line) {
    note.low_ledger_line = low_ledger_line;
  }
}

function last_note_case(current_glyph: GregorioGlyph, offset_pos: string,
  current_note: GregorioNoteElement, previous: GregorioNoteElement, no_ambitus_one: boolean): string {
  if (current_glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
    return 'FinalDeminutus';
  }
  if (!previous || (!no_ambitus_one &&
    (offset_pos == 'FinalUpperPunctum' ||
      previous.pitch - current_note.pitch == 1 ||
      current_note.pitch - previous.pitch == 1))) {
    return offset_pos;
  }
  if (previous.pitch < current_note.pitch) {
    if (current_glyph.notes.liquescentia &
      (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
      return 'FinalConnectedAuctus';
    } else {
      return 'FinalConnectedVirga';
    }
  } else {
    return 'FinalConnectedPunctum';
  }
}

function compute_h_episema(
  h: HeightComputation,
  element: GregorioElement,
  element_i: number,
  glyph: GregorioGlyph,
  glyph_i: number,
  note: GregorioNoteElement,
  next: GregorioNoteElement,
  next_next: GregorioNoteElement,
  note_i: number,
  type: GtexType,
  score: GregorioScore,
  elements: GregorioElement[],
): void {
  let size: GrehepisemaSize;

  if (h.is_applicable(note)) {
    if (h.is_shown(note)) {
      size = h.get_size(note);

      if (h.active) {
        const nextHeight = compute_h_episema_height(glyph, note, next, next_next, h.vpos);
        if (h.connected && is_connected_left(size)
          && (note_i !== 1 || is_connectable_interglyph_ambitus(nextHeight, h.height))) {
          if (h.is_better_height(nextHeight, h.height)) {
            h.height = nextHeight;
          }
        } else {
          end_h_episema(h, glyph, note, note_i, glyph_i, element_i, score, elements);
          start_h_episema(h, element, element_i, glyph, glyph_i, note, note_i, next, next_next);
        }
      } else {
        start_h_episema(h, element, element_i, glyph, glyph_i, note, note_i, next, next_next);
      }

      h.connected = h.is_connected(note) && is_connected_right(size);
      h.last_connected_element_i = element_i;
      h.last_connected_glyph_i = glyph_i;
      h.last_connected_note_i = note_i;
    } else {
      end_h_episema(h, glyph, note, note_i, glyph_i, element_i, score, elements);
    }
  } else if (!h.is_shown(note)) {
    switch (type) {
      case GtexType.T_PORRECTUS:
      case GtexType.T_PORRECTUS_FLEXUS:
        if (note_i === 2) {
          end_h_episema(h, glyph, note, note_i, glyph_i, element_i, score, elements);
        }
        break;

      case GtexType.T_TORCULUS_RESUPINUS:
      case GtexType.T_TORCULUS_RESUPINUS_FLEXUS:
        if (note_i === 3) {
          end_h_episema(h, glyph, note, note_i, glyph_i, element_i, score, elements);
        }
        break;

      default:
        // do nothing
        break;
    }
  }
}

function high_low_set_upper(glyph: GregorioGlyph, note: GregorioNoteElement): void {
  if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
    note.is_upper_note = true;
  }
}

function below_if_next_ambitus_allows(note: GregorioNoteElement, next: GregorioNoteElement): GregorioVPosition {
  if (!next || note.pitch - next.pitch > 2) {
    return GregorioVPosition.BELOW;
  }
  return GregorioVPosition.ABOVE;
}


function note_before_last_note_case_ignoring_deminutus(current_note: GregorioNoteElement,
  next: GregorioNoteElement, previous: GregorioNoteElement): string {
  if (!next) {
    return '';
    // throw new Error('current_note.next is null');
  }
  if ((current_note.pitch - next.pitch) == 1
    || (current_note.pitch - next.pitch) == -1) {
    if (!previous || current_note.pitch - previous.pitch > 1) {
      return 'ConnectedPenultBeforePunctumOne';
    } else {
      return 'PenultBeforePunctumOne';
    }
  } else {
    if (!previous || current_note.pitch - previous.pitch > 1) {
      return 'ConnectedPenultBeforePunctumWide';
    } else {
      return 'PenultBeforePunctumWide';
    }
  }
}

function low_high_set_upper(glyph: GregorioGlyph, note: GregorioNoteElement): void {
  if ((glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) ||
    !(glyph.notes.liquescentia
      & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS))) {
    note.is_upper_note = true;
  }
}

function high_low_set_lower(glyph: GregorioGlyph, note: GregorioNoteElement): void {
  if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
    note.is_lower_note = true;
  }
}

function above_if_either_h_episema(next: GregorioNoteElement,
  previous: GregorioNoteElement): GregorioVPosition {
  if ((previous && previous.h_episema_above) || (next && next.h_episema_above)) {
    return GregorioVPosition.ABOVE;
  }
  return GregorioVPosition.BELOW;
}

function advise_positioning(
  glyphs: GregorioGlyph[], glyph_i_arg: number,
  note: GregorioNoteElement, note_i_arg: number, type: GtexType): GregorioVPosition {
  let h_episema: GregorioVPosition = GregorioVPosition.AUTO;
  let v_episema: GregorioVPosition = GregorioVPosition.AUTO;
  let v_episema_below_is_lower = false;
  let done: boolean;
  let previous: GregorioGlyph | null;
  let i = note_i_arg;
  /* no need to clear is_lower_note/is_upper_note because we used calloc */

  const glyph = glyphs[glyph_i_arg];
  const next = glyph.notes.notes[i + 1];
  const next_next = glyph.notes.notes[i + 2];
  const prev = glyph.notes.notes[i - 1];

  i += 1; // todo: some trash from gregorio - fix to use normal indexes
  switch (type) {
    case GtexType.T_PES:
    case GtexType.T_QUILISMA_PES:
      /*
       * in the case of a pes, we put the episema just under the bottom note 
       */
      if (i == 1) {
        if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
          note.gtex_offset_case = 'InitioDebilis';
          h_episema = above_if_h_episema(next);
        } else {
          note.is_lower_note = true;
          if (note.shape == GregorioShape.QUILISMA) {
            note.gtex_offset_case = 'FinalQuilisma';
          } else {
            note.gtex_offset_case = 'FinalPunctum';
          }
          h_episema = GregorioVPosition.BELOW;
        }
        v_episema = GregorioVPosition.BELOW;
      } else { /* i=2  */
        if (!(glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS)) {
          note.is_upper_note = true;
        }
        if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
          note.gtex_offset_case = 'FinalDeminutus';
        } else {
          note.gtex_offset_case = 'FinalUpperPunctum';
        }
        h_episema = GregorioVPosition.ABOVE;
        v_episema = below_if_auctus(glyph);
      }
      break;
    case GtexType.T_PES_QUADRATUM:
    case GtexType.T_PES_QUASSUS:
    case GtexType.T_QUILISMA_PES_QUADRATUM:
      if (i == 1) {
        note.gtex_offset_case = first_note_case(note, next, glyph);
        h_episema = above_if_h_episema(next);
        v_episema = GregorioVPosition.BELOW;
      } else { /* i=2 */
        if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
          note.gtex_offset_case = 'InitioDebilis';
        } else {
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum',
            note, prev, true);
        }
        h_episema = GregorioVPosition.ABOVE;
        v_episema = GregorioVPosition.BELOW;
      }
      break;
    case GtexType.T_FLEXUS:
    case GtexType.T_FLEXUS_ORISCUS:
      if (i == 1) {
        high_low_set_upper(glyph, note);
        if (type == GtexType.T_FLEXUS_ORISCUS) {
          note.gtex_offset_case = 'InitialConnectedOriscus';
        } else {
          if (next && note.pitch - next.pitch == 1) {
            note.gtex_offset_case = 'InitialVirga';
          } else {
            note.gtex_offset_case = 'InitialConnectedVirga';
          }
        }
        h_episema = GregorioVPosition.ABOVE;
        if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
          v_episema = below_if_next_ambitus_allows(note, next);
        } else {
          v_episema = GregorioVPosition.BELOW;
        }
      } else { /* i=2 */
        high_low_set_lower(glyph, note);
        note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev, false);
        if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
          h_episema = GregorioVPosition.BELOW;
        } else {
          h_episema = above_if_h_episema(prev);
        }
        v_episema = GregorioVPosition.BELOW;
      }
      break;
    case GtexType.T_TORCULUS_RESUPINUS_FLEXUS:
      done = true;
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = above_if_h_episema(next);
          v_episema = GregorioVPosition.BELOW;
          break;
        case HEPISEMA_FIRST_TWO:
        case 2:
          if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
            note.gtex_offset_case = 'TorcResDebilisAuctusSecondAnyAny';
          } else {
            if (note.pitch - prev.pitch == 1) {
              switch (prev.shape) {
                case GregorioShape.QUILISMA:
                  note.gtex_offset_case =
                    'TorcResQuilismaAuctusSecondOneAny';
                  break;
                case GregorioShape.ORISCUS_ASCENDENS:
                case GregorioShape.ORISCUS_DESCENDENS:
                  note.gtex_offset_case =
                    'TorcResOriscusAuctusSecondOneAny';
                  break;
                default:
                  note.gtex_offset_case = 'TorcResAuctusSecondOneAny';
                  break;
              }
            } else {
              switch (prev.shape) {
                case GregorioShape.QUILISMA:
                  note.gtex_offset_case =
                    'TorcResQuilismaAuctusSecondWideAny';
                  break;
                case GregorioShape.ORISCUS_ASCENDENS:
                case GregorioShape.ORISCUS_DESCENDENS:
                  note.gtex_offset_case =
                    'TorcResOriscusAuctusSecondWideAny';
                  break;
                default:
                  note.gtex_offset_case = 'TorcResAuctusSecondWideAny';
                  break;
              }
            }
          }
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          v_episema_below_is_lower = true;
          break;
        default:
          --i;
          done = false;
          break;
      }

      if (done) {
        break;
      }
    /* fall through */
    case GtexType.T_PORRECTUS_FLEXUS:
      switch (i) {
        case HEPISEMA_FIRST_TWO:
        case 1:
          note.gtex_offset_case = 'PorrAuctusInitialAny';
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          v_episema_below_is_lower = true;
          break;
        case 2:
          if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
            note.gtex_offset_case = 'AntepenultBeforeDeminutus';
          } else {
            note.gtex_offset_case = 'AntepenultBeforePunctum';
          }
          h_episema = above_if_either_h_episema(next, prev);
          v_episema = GregorioVPosition.BELOW;
          break;
        case 3:
          high_low_set_upper(glyph, note);
          h_episema = GregorioVPosition.ABOVE;
          if ((glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) && next) {
            v_episema = below_if_next_ambitus_allows(note, next);
            note.gtex_offset_case = 'PenultBeforeDeminutus';
          } else {
            v_episema = GregorioVPosition.BELOW;
            note.gtex_offset_case = note_before_last_note_case(glyph, note, next, prev);
          }
          break;
        default:
          high_low_set_lower(glyph, note);
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev, false);
          h_episema = above_if_h_episema(prev);
          v_episema = GregorioVPosition.BELOW;
          break;
      }
      break;
    case GtexType.T_TORCULUS_RESUPINUS:
      done = true;
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = above_if_h_episema(next);
          v_episema = GregorioVPosition.BELOW;
          break;
        case HEPISEMA_FIRST_TWO:
        case 2:
          if (glyph.notes.liquescentia &
            (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
            /* auctus */
            if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
              note.gtex_offset_case = 'TorcResDebilisAuctusSecondAnyAny';
            } else {
              if (note.pitch - prev.pitch == 1) {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaAuctusSecondOneAny';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusAuctusSecondOneAny';
                    break;
                  default:
                    note.gtex_offset_case = 'TorcResAuctusSecondOneAny';
                    break;
                }
              } else {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaAuctusSecondWideAny';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusAuctusSecondWideAny';
                    break;
                  default:
                    note.gtex_offset_case = 'TorcResAuctusSecondWideAny';
                    break;
                }
              }
            }
          } else if (next_next?.pitch - next.pitch == 1) {
            /* non-auctus with a second ambitus of 1 */
            if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
              note.gtex_offset_case =
                'TorcResDebilisNonAuctusSecondAnyOne';
            } else {
              if (note.pitch - prev.pitch == 1) {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaNonAuctusSecondOneOne';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusNonAuctusSecondOneOne';
                    break;
                  default:
                    note.gtex_offset_case =
                      'TorcResNonAuctusSecondOneOne';
                    break;
                }
              } else {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaNonAuctusSecondWideOne';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusNonAuctusSecondWideOne';
                    break;
                  default:
                    note.gtex_offset_case =
                      'TorcResNonAuctusSecondWideOne';
                    break;
                }
              }
            }
          } else {
            /* non-auctus with a second ambitus of at least 2 */
            if (glyph.notes.liquescentia & GregorioLiquescentia.INITIO_DEBILIS) {
              note.gtex_offset_case =
                'TorcResDebilisNonAuctusSecondAnyWide';
            } else {
              if (note.pitch - prev.pitch == 1) {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaNonAuctusSecondOneWide';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusNonAuctusSecondOneWide';
                    break;
                  default:
                    note.gtex_offset_case =
                      'TorcResNonAuctusSecondOneWide';
                    break;
                }
              } else {
                switch (prev.shape) {
                  case GregorioShape.QUILISMA:
                    note.gtex_offset_case =
                      'TorcResQuilismaNonAuctusSecondWideWide';
                    break;
                  case GregorioShape.ORISCUS_ASCENDENS:
                  case GregorioShape.ORISCUS_DESCENDENS:
                    note.gtex_offset_case =
                      'TorcResOriscusNonAuctusSecondWideWide';
                    break;
                  default:
                    note.gtex_offset_case =
                      'TorcResNonAuctusSecondWideWide';
                    break;
                }
              }
            }
          }
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          v_episema_below_is_lower = true;
          break;
        default:
          --i;
          done = false;
          break;
      }

      if (done) {
        break;
      }
    /* fall through */
    case GtexType.T_PORRECTUS: /* or porrectus-like flexus */
      switch (i) {
        case HEPISEMA_FIRST_TWO:
        case 1:
          if (glyph.notes.fuse_to_next_glyph ||
            (glyph.notes.liquescentia
              & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS))) {
            /* auctus */
            note.gtex_offset_case = 'PorrAuctusInitialAny';
          } else if (next_next?.pitch - next.pitch == 1) {
            /* non-auctus with a second ambitus of 1 */
            note.gtex_offset_case = 'PorrNonAuctusInitialOne';
          } else {
            /* non-auctus with a second ambitus of at least 2 */
            note.gtex_offset_case = 'PorrNonAuctusInitialWide';
          }
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          v_episema_below_is_lower = true;
          break;
        case 2:
          low_high_set_lower(glyph, note);
          if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
            note.gtex_offset_case = 'PenultBeforeDeminutus';
          } else if (glyph.notes.liquescentia
            & (GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.AUCTUS_DESCENDENS)) {
            note.gtex_offset_case = 'PenultBeforePunctumWide';
          } else {
            note.gtex_offset_case = 'FinalPunctum';
          }
          h_episema = above_if_h_episema(prev);
          if (h_episema == GregorioVPosition.BELOW && !next) {
            /* handle porrectus-like flexus */
            h_episema = above_if_fused_next_h_episema(glyphs, glyph_i_arg);
          }
          v_episema = GregorioVPosition.BELOW;
          break;
        default: /* case 3 */
          low_high_set_upper(glyph, note);
          note.gtex_offset_case = last_note_case(glyph, 'FinalUpperPunctum',
            note, prev, true);
          h_episema = GregorioVPosition.ABOVE;
          v_episema = below_if_auctus(glyph);
          break;
      }
      break;
    case GtexType.T_SCANDICUS:
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = GregorioVPosition.BELOW;
          v_episema = GregorioVPosition.BELOW;
          break;
        case 2:
          low_high_set_lower(glyph, note);
          if (note.pitch - prev.pitch == 1) {
            note.gtex_offset_case = 'FinalPunctum';
          } else {
            note.gtex_offset_case = 'FinalConnectedPunctum';
          }
          h_episema = GregorioVPosition.BELOW;
          v_episema = GregorioVPosition.BELOW;
          break;
        default:
          low_high_set_upper(glyph, note);
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev, true);
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          break;
      }
      break;
    case GtexType.T_SALICUS_FLEXUS:
      done = true;
      v_episema = GregorioVPosition.BELOW;
      switch (i) {
        case 3:
          note.gtex_offset_case = note_before_last_note_case(glyph, note, next, prev);
          h_episema = GregorioVPosition.ABOVE;
          break;
        case 4:
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev, false);
          h_episema = GregorioVPosition.BELOW;
          break;
        default:
          done = false;
          break;
      }
      if (done) {
        break;
      }
    /* fall through */
    case GtexType.T_SALICUS:
      v_episema = GregorioVPosition.BELOW;
      switch (i) {
        case 1:
          note.gtex_offset_case = 'InitialConnectedPunctum';
          h_episema = above_if_h_episema(next);
          break;
        case 2:
          if (next.pitch - note.pitch == 1) {
            note.gtex_offset_case = 'SalicusOriscusOne';
          } else {
            note.gtex_offset_case = 'SalicusOriscusWide';
          }
          h_episema = GregorioVPosition.ABOVE;
          break;
        default:
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev, false);
          if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
            v_episema = GregorioVPosition.ABOVE;
          }
          h_episema = GregorioVPosition.ABOVE;
          break;
      }
      break;
    case GtexType.T_TORCULUS_LIQUESCENS:
      done = true;
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = above_if_h_episema(next);
          v_episema = GregorioVPosition.BELOW;
          break;
        case 2:
          note.gtex_offset_case =
            note_before_last_note_case_ignoring_deminutus(note, next, prev);
          h_episema = GregorioVPosition.ABOVE;
          v_episema = GregorioVPosition.BELOW;
          break;
        default:
          --i;
          done = false;
          break;
      }

      if (done) {
        break;
      }
    /* fall through */
    case GtexType.T_ANCUS:
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = above_if_h_episema(next);
          v_episema = GregorioVPosition.BELOW;
          break;
        case 2:
          note.is_upper_note = true;
          note.gtex_offset_case = note_before_last_note_case(glyph, note, next, prev);
          h_episema = GregorioVPosition.ABOVE;
          v_episema = below_if_next_ambitus_allows(note, next);
          break;
        default:
          note.is_lower_note = true;
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev,
            false);
          h_episema = GregorioVPosition.BELOW;
          v_episema = GregorioVPosition.BELOW;
          break;
      }
      break;
    case GtexType.T_TORCULUS:
      switch (i) {
        case 1:
          note.gtex_offset_case = first_note_case(note, next, glyph);
          h_episema = above_if_h_episema(next);
          v_episema = GregorioVPosition.BELOW;
          break;
        case 2:
          high_low_set_upper(glyph, note);
          note.gtex_offset_case = note_before_last_note_case(glyph, note, next, prev);
          h_episema = GregorioVPosition.ABOVE;
          if (glyph.notes.liquescentia & GregorioLiquescentia.DEMINUTUS) {
            v_episema = below_if_next_ambitus_allows(note, next);
          } else {
            v_episema = GregorioVPosition.BELOW;
          }
          break;
        default:
          high_low_set_lower(glyph, note);
          note.gtex_offset_case = last_note_case(glyph, 'FinalPunctum', note, prev,
            false);
          h_episema = above_if_h_episema(prev);
          v_episema = GregorioVPosition.BELOW;
          break;
      }
      break;
    default: /* case T_ONE_NOTE */
      previous = gregorio_previous_non_texverb_glyph(glyphs, glyph_i_arg);
      if ((!previous || previous.type != GregorioType.GLYPH
        || previous.notes.fuse_to_next_glyph < 0)
        && (glyph.notes.fuse_to_next_glyph > 0)) {
        h_episema = above_if_fused_next_h_episema(glyphs, glyph_i_arg);
      } else {
        h_episema = GregorioVPosition.ABOVE;
      }
      v_episema = GregorioVPosition.BELOW;
      switch (note.shape) {
        case GregorioShape.PUNCTUM_INCLINATUM_DEMINUTUS:
          note.gtex_offset_case = 'FinalInclinatumDeminutus';
          break;
        case GregorioShape.PUNCTUM_INCLINATUM_AUCTUS:
        case GregorioShape.PUNCTUM_INCLINATUM_ASCENDENS:
        case GregorioShape.PUNCTUM_INCLINATUM_DESCENDENS:
          note.gtex_offset_case = 'FinalInclinatum';
          break;
        case GregorioShape.STROPHA:
          note.gtex_offset_case = 'FinalStropha';
          break;
        case GregorioShape.QUILISMA:
          note.gtex_offset_case = fused_single_note_case(glyph,
            'FinalQuilisma', 'LeadingQuilisma');
          break;
        case GregorioShape.ORISCUS_ASCENDENS:
        case GregorioShape.ORISCUS_DESCENDENS:
        case GregorioShape.ORISCUS_DEMINUTUS:
          note.gtex_offset_case = fused_single_note_case(glyph, 'FinalOriscus',
            'LeadingOriscus');
          break;
        case GregorioShape.VIRGA:
          note.gtex_offset_case = 'InitialVirga';
          break;
        case GregorioShape.VIRGA_REVERSA:
          if (glyph.notes.liquescentia == GregorioLiquescentia.AUCTUS_DESCENDENS) {
            note.gtex_offset_case = 'FinalVirgaAuctus';
          } else {
            note.gtex_offset_case = 'InitialVirga';
          }
          break;
        case GregorioShape.LINEA_PUNCTUM:
          note.gtex_offset_case = 'FinalLineaPunctum';
          break;
        case GregorioShape.LINEA:
          note.gtex_offset_case = 'FinalPunctum';
          break;
        case GregorioShape.FLAT:
          note.gtex_offset_case = 'Flat';
          break;
        case GregorioShape.FLAT_PAREN:
          note.gtex_offset_case = 'FlatParen';
          break;
        case GregorioShape.SHARP:
          note.gtex_offset_case = 'Sharp';
          break;
        case GregorioShape.SHARP_PAREN:
          note.gtex_offset_case = 'SharpParen';
          break;
        case GregorioShape.NATURAL:
          note.gtex_offset_case = 'Natural';
          break;
        case GregorioShape.NATURAL_PAREN:
          note.gtex_offset_case = 'NaturalParen';
          break;
        default:
          note.gtex_offset_case = last_note_case(glyph,
            fused_single_note_case(glyph, 'FinalPunctum', 'LeadingPunctum'),
            note, prev, true);
          break;
      }
      break;
  }

  if (note.signs & GregorioSign.V_EPISEMA) {
    if (note.v_episema_height) {
      if (note.v_episema_height > note.pitch) {
        /* above is always higher because of GregorioTeX's design */
        note.v_episema_height += GregorioVPosition.ABOVE;
      }
    } else {
      note.v_episema_height = note.pitch + v_episema;
      if ((v_episema == GregorioVPosition.BELOW && v_episema_below_is_lower)
        || v_episema == GregorioVPosition.ABOVE) {
        /* above is always higher because of GregorioTeX's design */
        note.v_episema_height += v_episema;
      }
    }
  }

  return h_episema;
}

function isHEpisemaAboveApplicable(note: GregorioNoteElement): boolean {
  return note.h_episema_above === HEPISEMA.NONE || !note.is_lower_note;
}

function isHEpisemaBelowApplicable(note: GregorioNoteElement): boolean {
  return note.h_episema_below === HEPISEMA.NONE || !note.is_upper_note;
}

function gtexIsHEpisemaAboveShown(note: GregorioNoteElement): boolean {
  return note.h_episema_above !== HEPISEMA.NONE;
}

function gtexIsHEpisemaBelowShown(note: GregorioNoteElement): boolean {
  return note.h_episema_below !== HEPISEMA.NONE;
}

function isHEpisemaAboveConnected(note: GregorioNoteElement): boolean {
  return note.h_episema_above_connect;
}

function isHEpisemaBelowConnected(note: GregorioNoteElement): boolean {
  return note.h_episema_below_connect;
}

function getHEpisemaAboveSize(note: GregorioNoteElement): GrehepisemaSize {
  return note.h_episema_above_size;
}

function isHEpisemaAboveBetterHeight(newHeight: number, oldHeight: number): boolean {
  return newHeight > oldHeight;
}

function gregorioPositionHEpisemaAbove(note: GregorioNoteElement, height: number, connect: boolean): void {
  if (!note || (note.type !== GregorioType.NOTE && note.type !== GregorioType.BAR)) {
    // throw new Error('Assertion failed: note is not defined or its type is not GRE_NOTE or GRE_BAR');
    return;
  }
  note.h_episema_above = height;
  note.h_episema_above_connect = connect;
}

function getHEpisemaBelowSize(note: GregorioNoteElement): GrehepisemaSize {
  return note.h_episema_below_size;
}

function isHEpisemaBelowBetterHeight(newHeight: number, oldHeight: number): boolean {
  return newHeight < oldHeight;
}

function gregorioPositionHEpisemaBelow(note: GregorioNoteElement, height: number, connect: boolean): void {
  if (!note || (note.type !== GregorioType.NOTE && note.type !== GregorioType.BAR)) {
    // throw new Error('Assertion failed: note is not defined or its type is not GRE_NOTE or GRE_BAR');
    return;
  }
  note.h_episema_below = height;
  note.h_episema_below_connect = connect;
}

function empty_above_computation(elements: GregorioElement[]): HeightComputation {
  return new HeightComputation({
    vpos: GregorioVPosition.ABOVE,
    orientation: GregorioSignOrientation.SO_OVER,
    is_applicable: isHEpisemaAboveApplicable,
    is_shown: gtexIsHEpisemaAboveShown,
    is_connected: isHEpisemaAboveConnected,
    get_size: getHEpisemaAboveSize,
    is_better_height: isHEpisemaAboveBetterHeight,
    position: gregorioPositionHEpisemaAbove,
    active: false,
    height: 0,
    connected: false,
    start_element_i: null,
    start_glyph_i: null,
    start_note_i: null,
    last_connected_element_i: null,
    last_connected_glyph_i: null,
    last_connected_note_i: null,
    elements: elements,
  });
}

function empty_below_computation(elements: GregorioElement[]) {
  return new HeightComputation({
    vpos: GregorioVPosition.BELOW,
    orientation: GregorioSignOrientation.SO_UNDER,
    is_applicable: isHEpisemaBelowApplicable,
    is_shown: gtexIsHEpisemaBelowShown,
    is_connected: isHEpisemaBelowConnected,
    get_size: getHEpisemaBelowSize,
    is_better_height: isHEpisemaBelowBetterHeight,
    position: gregorioPositionHEpisemaBelow,
    active: false,
    height: 0,
    connected: false,
    start_element_i: null,
    start_glyph_i: null,
    start_note_i: null,
    last_connected_element_i: null,
    last_connected_glyph_i: null,
    last_connected_note_i: null,
    elements: elements,
  });
}

export function gregoriotex_compute_positioning(elements: GregorioElement[], score: GregorioScore): void {
  const above = empty_above_computation(elements);
  const below = empty_below_computation(elements);

  let glyph: GregorioGlyph | null = null;

  for (let element_i = 0; element_i < elements.length; element_i++) {
    const element = elements[element_i];

    if (element.type === GregorioType.ELEMENT) {
      for (let glyph_i = 0; glyph_i < element.glyphs.length; glyph_i++) {
        glyph = element.glyphs[glyph_i];

        if (glyph.type === GregorioType.GLYPH) {
          glyph.notes.fuse_to_next_glyph = compute_fused_shift(element.glyphs, glyph_i);
          const [_, _2, gtype] = gregoriotex_determine_glyph_name(element.glyphs, glyph_i);

          for (let note_i = 0; note_i < glyph.notes.notes.length; note_i++) {
            const note = glyph.notes.notes[note_i];
            const note_next = glyph.notes.notes[note_i + 1];
            const note_next_next = glyph.notes.notes[note_i + 2];
            if (note.type == GregorioType.NOTE) {
              compute_note_positioning(above, below, element, element_i, glyph_i,
                note, note_next, note_next_next, note_i, gtype, score, elements);
            }
          }
        }
      }
    } else if (!is_bridgeable_space(element)) {
      end_h_episema(above, glyph!, null, null, null, null, score, elements);
      end_h_episema(below, glyph!, null, null, null, null, score, elements);
    }
  }

  end_h_episema(above, glyph!, null, null, null, null, score, elements);
  end_h_episema(below, glyph!, null, null, null, null, score, elements);
}

function compute_height_extrema(glyph: GregorioGlyph, status: gregoriotex_status): void {
  let height: number;

  for (let note_i = 0; note_i < glyph.notes.notes.length; ++note_i) {
    const note = glyph.notes.notes[note_i];
    const note_next = glyph.notes.notes[note_i + 1];
    const note_next_next = glyph.notes.notes[note_i + 2];

    if (note.h_episema_above) {
      height = note.h_episema_above;
    } else {
      if (note.v_episema_height && note.v_episema_height > note.pitch) {
        height = note.v_episema_height;
      } else {
        height = note.pitch;
      }

      if (note.choral_sign && !choral_sign_here_is_low(glyph, note, note_next, note_next_next, null)) {
        ++height;
      }
    }
    if (status.top_height === UNDETERMINED_HEIGHT || height > status.top_height) {
      status.top_height = height;
    }

    if (note.h_episema_below) {
      height = note.h_episema_below;
    } else if (note.v_episema_height && note.v_episema_height < note.pitch) {
      height = note.v_episema_height;
    } else {
      height = note.pitch;

      if (glyph.type !== GregorioType.GLYPH) {
        throw new Error('glyph.type must be GRE_GLYPH here');
      }
      switch (glyph.notes.glyph_type) {
        case GregorioGlyphType.BIVIRGA:
        case GregorioGlyphType.TRIVIRGA:
        case GregorioGlyphType.VIRGA:
        case GregorioGlyphType.VIRGA_REVERSA:
          --height;
          break;

        case GregorioGlyphType.PUNCTUM:
          switch (note.shape) {
            case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
            case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
              --height;
              break;

            default:
              // default to avoid the warning
              break;
          }
          break;

        default:
          // default to avoid the warning

          // other shapes like the porrectus have stems, but the height
          // computation will cover other notes in the shape, negating
          // the need to account for the stem on the shape
          break;
      }
    }

    if (status.bottom_height === UNDETERMINED_HEIGHT || height < status.bottom_height) {
      status.bottom_height = height;
    }
  }
}

export function gregoriotex_step3(elements: GregorioElement[], status: gregoriotex_status): void {
  for (let note_i = 0; note_i < elements.length; ++note_i) {
    const element = elements[note_i];

    switch (element.type) {
      case GregorioType.CUSTOS:
        // last_of_voice[voice] = element;
        break;

      case GregorioType.ELEMENT:
        for (let glyph_i = 0; glyph_i < element.glyphs.length; ++glyph_i) {
          const glyph = element.glyphs[glyph_i];

          if (glyph.type === GregorioType.GLYPH) {
            // last_of_voice[voice] = element;
            compute_height_extrema(glyph, status);
          }
        }
        break;

      case GregorioType.BAR:
        // last_of_voice[voice] = element;
        break;

      default:
        // to eliminate the warning
        break;
    }
  }
}

export function fixup_height_extrema(status: gregoriotex_status) {
  if (status.top_height === UNDETERMINED_HEIGHT) {
    status.top_height = 9; // 'g'
  }
  if (status.bottom_height === UNDETERMINED_HEIGHT) {
    status.bottom_height = 9; // 'g'
  }
}

export interface gregoriotex_status {
  bottom_line: boolean;
  top_height: number;
  bottom_height: number;
  suppressed_custos: boolean;
}
